/**
 * BatchReportPage — Production Report module entry tại
 * `/factory/<code>/report/batch-report`.
 *
 * Spec: `frontend/plan/Excel_Production_Report_Web_UI_Conversion_Spec.md`.
 *
 * Module wrap 6 tab nội bộ tương ứng 6 sheet Excel:
 *   1. Dashboard            — executive KPI + 6 chart blocks (§8).
 *   2. Batch Report         — INPUT/OUTPUT grid (§3).
 *   3. Production Analysis  — budget/actual + loss + extracted value (§4).
 *   4. Thông tin qui chuẩn  — standardization grid (§5).
 *   5. Comparison           — current vs previous PO (§6).
 *   6. BOM-BTA1             — BOM template/config + costs (§7).
 *
 * Shared state:
 * - `poNo`             — PO selector (sync across tabs, §1.3 batch context).
 * - `mode`             — `qui chuan` / `chua qui chuan` toggle (§4.3 D3).
 * - `showAdvanced`     — Batch Report column expansion only.
 * - `activeTab`        — landing default = Dashboard (§1.2).
 *
 * Mock data hiện hardcode cho PO `PRO-TLBL-25097`. Khi BE expose
 * `/api/production-report/{po_no}/...` thì RTK Query thay block mock.
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useGetFactoryTabRowsQuery } from '@/store/api/factoryApi'
import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'

import {
  SAMPLE_INPUT_ROWS,
  SAMPLE_INPUT_VALUE,
  SAMPLE_OUTPUT_ROWS,
  SAMPLE_PO_NO,
} from './batchReportMock'
import { SAMPLE_PO_TYPE } from './productionReportMock'
import BatchReportTab from './tabs/BatchReportTab'
import BomTab from './tabs/BomTab'
import ComparisonTab from './tabs/ComparisonTab'
import DashboardTab from './tabs/DashboardTab'
import ProductionAnalysisTab from './tabs/ProductionAnalysisTab'
import StandardizationTab from './tabs/StandardizationTab'
import { COLORS } from './tabs/styleTokens'

type Mode = 'chua qui chuan' | 'qui chuan'

type TabKey =
  | 'dashboard'
  | 'batch-report'
  | 'production-analysis'
  | 'standardization'
  | 'comparison'
  | 'bom'

interface TabConfig {
  key: TabKey
  label: string
}

// Tab label cho `bom` được derive dynamic theo selectedBomCode (BOM-<code>),
// fallback BTA1 khi PO chưa khớp / chưa chọn. Các tab khác hard-code.
const STATIC_TABS: TabConfig[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'batch-report', label: 'Batch Report' },
  { key: 'production-analysis', label: 'Production Analysis' },
  { key: 'standardization', label: 'Thông tin qui chuẩn' },
  { key: 'comparison', label: 'Comparison' },
]

export interface BatchReportPageProps {
  factoryCode: string
  onBackToHub?: () => void
}

const TAB_KEYS: readonly TabKey[] = [
  'dashboard',
  'batch-report',
  'production-analysis',
  'standardization',
  'comparison',
  'bom',
] as const
const DEFAULT_TAB: TabKey = 'dashboard'

function isTabKey(v: string | undefined): v is TabKey {
  return !!v && (TAB_KEYS as readonly string[]).includes(v)
}

export default function BatchReportPage({ factoryCode, onBackToHub }: BatchReportPageProps) {
  const location = useLocation()
  const navigate = useNavigate()

  // URL segment 3 (sau /<code>/report/batch-report/<tabSlug>) là active tab.
  // Empty/invalid → fallback `dashboard`. Mỗi click tab → navigate URL mới
  // (browser back/forward + deep-link share work out-of-box).
  // URL pattern: segments[0]=factoryCode, [1]='report', [2]='batch-report', [3]=inner tab.
  const activeTab: TabKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    return isTabKey(segments[3]) ? segments[3] : DEFAULT_TAB
  }, [location.pathname])

  // Redirect các URL không canonical về `/dashboard`:
  //  - bare URL không có segment 3 (`/<code>/report/batch-report`)
  //  - segment 3 không phải tab key hợp lệ (vd typo)
  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const seg3 = segments[3]
    if (segments.length === 3 || (segments.length >= 4 && !isTabKey(seg3))) {
      navigate(`/${factoryCode}/report/batch-report/${DEFAULT_TAB}`, { replace: true })
    }
  }, [location.pathname, factoryCode, navigate])

  const setActiveTab = (key: TabKey) => {
    navigate(`/${factoryCode}/report/batch-report/${key}`)
  }

  const [poNo, setPoNo] = useState(SAMPLE_PO_NO)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [mode, setMode] = useState<Mode>('chua qui chuan')

  // PO List combo options — fetch từ factory tab `po-list` với date range
  // RỘNG (2000–2099) để cover mọi PO lịch sử bất kể factory hub filter hiện
  // tại. Lý do: user vào BatchReport thường muốn xem PO cũ (2024, 2025...)
  // mà có thể nằm ngoài date range default của factory hub (~current year).
  // BE `_list_po` filter `po_date BETWEEN start AND end` strict, không có
  // bypass param → workaround bằng range rộng.
  const { data: poListRes } = useGetFactoryTabRowsQuery(
    {
      factoryCode,
      tab: 'po-list',
      startDate: '2000-01-01',
      endDate: '2099-12-31',
    },
    { skip: !factoryCode },
  )
  const poNoOptions = useMemo(() => {
    const items = poListRes?.items ?? []
    const seen = new Set<string>()
    const out: string[] = []
    for (const row of items) {
      const v = row['po_no']
      const s = v == null ? '' : String(v).trim()
      if (s && !seen.has(s)) {
        seen.add(s)
        out.push(s)
      }
    }
    return out
  }, [poListRes])

  // Mỗi PO List row có cột `bom_code` (Access form PoList ComboBox `public_v_factory_bom`).
  // BOM-BTA1 tab load BOM theo PO No đang chọn → tra cứu row khớp po_no rồi
  // pull bom_code. Empty string khi chưa match → BomTab fallback giá trị mock.
  // - So sánh po_no case-insensitive + trim để cover input gõ tay khác case.
  // - Defensive đọc bom_code lẫn camelCase bomCode trong trường hợp BE response
  //   serializer khác convention (current `_row()` giữ snake_case nhưng đề
  //   phòng tương lai).
  const selectedBomCode = useMemo(() => {
    const items = poListRes?.items ?? []
    const needle = poNo.trim().toUpperCase()
    if (!needle) return ''
    const row = items.find(
      (r) => String(r['po_no'] ?? '').trim().toUpperCase() === needle,
    )
    if (!row) return ''
    const raw = (row['bom_code'] ?? row['bomCode'] ?? '') as string | number | boolean
    return String(raw).trim()
  }, [poListRes, poNo])

  // FIXME(api): swap mock với endpoint thực khi BE sẵn sàng. PoNo input không
  // ảnh hưởng data hiện tại (chỉ display vì mock chỉ có 1 PO `PRO-TLBL-25097`).
  const inputRows = SAMPLE_INPUT_ROWS
  const outputRows = SAMPLE_OUTPUT_ROWS
  const inputValue = SAMPLE_INPUT_VALUE

  // §3.3 K1/K2 — đếm rows có StackID.
  const inputRowCount = useMemo(
    () => inputRows.filter((r) => r.stackId != null && r.stackId !== '').length,
    [inputRows],
  )
  const outputRowCount = useMemo(
    () => outputRows.filter((r) => r.stackId != null && r.stackId !== '').length,
    [outputRows],
  )

  const showAdvancedToggle = activeTab === 'batch-report'

  const TABS = useMemo<TabConfig[]>(() => [
    ...STATIC_TABS,
    { key: 'bom', label: `BOM-${selectedBomCode || 'BTA1'}` },
  ], [selectedBomCode])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/*
       * Title row — Back · Title · factory chip · subtitle stats inline ·
       * spacer · interactive controls (PO + mode + advanced).
       *
       * Stats hiển thị dạng subtitle compact ngay sau title (dot-separated
       * không có label) để KHÔNG wrap thành row riêng khi viewport hẹp —
       * vẫn là context info nhưng giờ chỉ tốn ~180px thay vì ~340px.
       */}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{
          py: 0.5,
          px: 0.25,
          rowGap: 0.5,
        }}
      >
        {onBackToHub ? (
          <Button
            size="small"
            onClick={onBackToHub}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            variant="text"
            sx={{
              textTransform: 'none',
              fontSize: '0.8125rem',
              color: COLORS.textMuted,
              minWidth: 0,
              px: 0.75,
              py: 0.25,
              flexShrink: 0,
            }}
            title="Reports"
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Reports</Box>
          </Button>
        ) : null}
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: COLORS.text, letterSpacing: 0.2 }}>
          Production Report
        </Typography>
        <Chip
          size="small"
          label={factoryCode.toUpperCase()}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: COLORS.primarySoft,
            color: COLORS.primary,
            border: 'none',
          }}
        />

        {/* Stats inline (subtitle metadata). Dot-separated, no labels.
            Hide on mobile xs — chiếm dòng, đẩy controls xuống wrap. */}
        <Typography
          sx={{
            fontSize: '0.78rem',
            color: COLORS.textMuted,
            display: { xs: 'none', sm: 'inline-flex' },
            alignItems: 'center',
            gap: 0.75,
            '& > span.sep': { color: COLORS.textSubtle },
            '& > b': { color: COLORS.text, fontWeight: 700 },
          }}
        >
          <b>{SAMPLE_PO_TYPE}</b>
          <span className="sep">·</span>
          <b>${inputValue.toLocaleString('en-US')}</b>
          <span className="sep">·</span>
          <b>{inputRowCount}</b> in
          <span className="sep">/</span>
          <b>{outputRowCount}</b> out
        </Typography>

        {/* Spacer push controls về phải. */}
        <Box sx={{ flex: 1, minWidth: 0 }} />

        <Autocomplete
          size="small"
          options={poNoOptions}
          // Fallback: nếu PO hiện tại (vd default mock) chưa có trong list →
          // append để Autocomplete không nhảy "no match", user vẫn thấy
          // selection đúng.
          value={poNo || null}
          onChange={(_e, v) => setPoNo(v ?? '')}
          getOptionLabel={(o) => o || ''}
          isOptionEqualToValue={(a, b) => a === b}
          filterOptions={(opts, state) => {
            const q = state.inputValue.toLowerCase().trim()
            // User đang search → trả về match thuần, KHÔNG inject current value
            // (tránh trùng khi search trả về chính PO khác mà current value
            // không khớp query).
            if (q) return opts.filter((o) => o.toLowerCase().includes(q))
            // Dropdown idle (mới mở, chưa gõ) → bảo đảm value đang chọn hiện
            // trong list (kể cả khi nó là legacy/mock không có trong options).
            if (poNo && !opts.includes(poNo)) return [poNo, ...opts]
            return opts
          }}
          sx={{
            width: { xs: 160, sm: 220, md: 260 },
            flexShrink: 0,
            bgcolor: COLORS.bgPage,
            borderRadius: 1,
            '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.4, px: 1 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="PO No." />
          )}
        />

        {/* Mode toggle — mirror `'Production Analysis'!D3`. */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ModeChip
            label="Non-std"
            tooltip="Non-standardized"
            active={mode === 'chua qui chuan'}
            onClick={() => setMode('chua qui chuan')}
          />
          <ModeChip
            label="Std"
            tooltip="Standardized"
            active={mode === 'qui chuan'}
            onClick={() => setMode('qui chuan')}
          />
        </Stack>

        {showAdvancedToggle ? (
          <FormControlLabel
            control={
              <Switch size="small" checked={showAdvanced} onChange={(_, v) => setShowAdvanced(v)} />
            }
            label={
              <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted, ...HIDE_ON_MOBILE_INLINE }}>
                Advanced
              </Typography>
            }
            sx={{ ml: 0, mr: 0 }}
          />
        ) : null}
      </Stack>

      {/*
       * Sub-nav row — Level-2 nav (pill / segmented control).
       *
       * Differentiation từ level-1 (top factory tabs, underline style):
       *   - Pill rounded background thay vì underline.
       *   - Active pill: solid primary bg + white text.
       *   - Wrapper bg `bgSubtle` + border → signal "section nội bộ".
       *
       * Pills full-width, scroll horizontal khi overflow (scrollbar mỏng để
       * user biết còn nội dung bên phải).
       */}
      <Box
        sx={{
          mt: 0.5,
          bgcolor: COLORS.bgSubtle,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 1.5,
          px: 0.5,
          py: 0.5,
          display: 'flex',
          gap: 0.5,
          overflowX: 'auto',
          // Thin scrollbar — user biết tab có thể overflow horizontal.
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { background: COLORS.borderStrong, borderRadius: 999 },
        }}
      >
        {TABS.map((t) => {
          const active = t.key === activeTab
          return (
            <Button
              key={t.key}
              size="small"
              disableElevation
              onClick={() => setActiveTab(t.key)}
              sx={{
                textTransform: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                borderRadius: 999,
                px: 1.5,
                py: 0.5,
                minHeight: 28,
                minWidth: 0,
                fontSize: '0.78rem',
                fontWeight: active ? 700 : 600,
                letterSpacing: 0.1,
                bgcolor: active ? COLORS.primary : 'transparent',
                color: active ? '#fff' : COLORS.textMuted,
                transition: 'background-color 120ms ease, color 120ms ease',
                '&:hover': {
                  bgcolor: active ? COLORS.primary : COLORS.bgPage,
                  color: active ? '#fff' : COLORS.text,
                },
              }}
            >
              {t.label}
            </Button>
          )
        })}
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 1.5 }}>
        {activeTab === 'dashboard' ? (
          <DashboardTab poNo={poNo} poType={SAMPLE_PO_TYPE} />
        ) : activeTab === 'batch-report' ? (
          <BatchReportTab inputRows={inputRows} outputRows={outputRows} showAdvanced={showAdvanced} />
        ) : activeTab === 'production-analysis' ? (
          <ProductionAnalysisTab />
        ) : activeTab === 'standardization' ? (
          <StandardizationTab />
        ) : activeTab === 'comparison' ? (
          <ComparisonTab poNo={poNo} />
        ) : (
          <BomTab bomCode={selectedBomCode} />
        )}
      </Box>
    </Box>
  )
}

/* ---------- helpers ------------------------------------------------------ */

function ModeChip({ label, tooltip, active, onClick }: { label: string; tooltip: string; active: boolean; onClick: () => void }) {
  return (
    <Chip
      size="small"
      clickable
      label={label}
      title={tooltip}
      onClick={onClick}
      sx={{
        height: 22,
        fontSize: '0.72rem',
        fontWeight: 600,
        px: 0.25,
        bgcolor: active ? COLORS.primary : 'transparent',
        color: active ? '#fff' : COLORS.textMuted,
        border: active ? 'none' : `1px solid ${COLORS.border}`,
        '&:hover': { bgcolor: active ? COLORS.primary : COLORS.bgHover },
      }}
    />
  )
}
